import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Box, Typography } from '@mui/material';
import { styled } from '@mui/system';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import {
  Coins,
  Wallet,
  CircuitBoard,
  BarChart3,
  Globe2,
  Gem,
  Building2
} from 'lucide-react';

// Category icon mapping
const categoryIcons = {
  'DeFi': Coins,
  'Wallet': Wallet,
  'NFT': Gem,
  'Media': BarChart3,
  'Bitcoin': CircuitBoard,
  'USA': Building2,
  'Other': Globe2,
};

// Enhanced hook/title at the top of each card
const TitleHook = styled(motion.div)(({ theme }) => ({
  fontSize: '1.4rem',
  fontWeight: 'bold',
  color: '#FFFFFF',
  textShadow: '0 0 10px rgba(255, 255, 255, 0.3)',
  marginBottom: '8px',
  textAlign: 'center',
  letterSpacing: '0.5px',
  lineHeight: '1.3',
  [theme.breakpoints.down('sm')]: {
    fontSize: '1.2rem',
  },
}));

// Category with icon
const CategorySection = styled('div')({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: '8px',
  marginBottom: '12px',
});

const CategoryIcon = styled(motion.div)({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: '6px',
  borderRadius: '50%',
  backgroundColor: 'rgba(255, 255, 255, 0.15)',
  border: '2px solid rgba(255, 255, 255, 0.4)',
  flexShrink: 0,
});

const CategoryText = styled('div')({
  color: 'rgba(255, 255, 255, 0.9)',
  fontSize: '0.9rem',
  fontWeight: 'bold',
  textTransform: 'uppercase',
  letterSpacing: '1px',
});

// Link name (shortened/clean version)
const LinkName = styled(motion.div)({
  fontSize: '1.1rem',
  fontWeight: 'bold',
  color: '#90EE90',
  textShadow: '0 0 5px rgba(144, 238, 144, 0.5)',
  marginBottom: '4px',
  textAlign: 'center',
  letterSpacing: '0.3px',
  cursor: 'pointer',
  '&:hover': {
    color: '#98FB98',
    textShadow: '0 0 8px rgba(144, 238, 144, 0.8)',
  }
});

// Full URL link
const FullUrlLink = styled(motion.a)({
  color: 'rgba(255, 255, 255, 0.7)',
  textDecoration: 'none',
  fontSize: '0.9rem',
  display: 'block',
  fontWeight: 'normal',
  textAlign: 'center',
  marginBottom: '8px',
  wordBreak: 'break-all',
  '&:hover': {
    color: '#90EE90',
    textDecoration: 'underline',
  }
});

// Description styling
const Description = styled('div')({
  color: 'rgba(255, 255, 255, 0.8)',
  fontSize: '1rem',
  lineHeight: '1.5',
  marginTop: '8px',
  textAlign: 'center',
  paddingTop: '8px',
  borderTop: '1px dotted rgba(255, 255, 255, 0.3)',
});

// Main styling components from your original code
const RetroText = styled(motion(Typography))(({ theme }) => ({
  color: '#FFFFFF',
  textShadow: `
    -1px -1px 0 #000,
    1px -1px 0 #000,
    -1px 1px 0 #000,
    1px 1px 0 #000
  `,
  fontWeight: 'bold',
  fontSize: '2.5rem',
  letterSpacing: '2px',
  textTransform: 'uppercase',
  fontFamily: "'Press Start 2P', cursive",
  cursor: 'pointer',
  [theme.breakpoints.down('sm')]: {
    fontSize: '1.2rem', // match the other section titles (Education & Resources)
  },
}));

const GreenDividerBar = styled(motion('div'), {
  shouldForwardProp: (prop) => prop !== 'bright',
})(({ theme, bright }) => ({
  width: '80%',
  height: '4px',
  backgroundColor: '#FFFFFF',
  margin: '20px auto 0',
  marginBottom: '30px',
  transformOrigin: 'center',
  boxShadow: `
    0 0 10px #FFFFFF,
    0 0 20px #00FF00,
    0 0 30px #00FF00,
    0 0 40px #00FF00,
    0 0 70px #FFFFFF,
    0 0 80px #FFFFFF,
    0 0 100px #00FF00,
    0 0 150px #00FF00
  `,
  // Brightens while the pointer is anywhere in the Popular Links section
  // (like the purple feature boxes on hover), dims when it leaves.
  filter: bright ? 'brightness(1.6)' : 'brightness(1)',
  transition: 'filter 0.5s ease',
  [theme.breakpoints.down('sm')]: {
    width: '95%',
  },
}));

export const GreenMotionDivider = ({ bright = false }) => {
  return (
    <GreenDividerBar
      bright={bright}
      initial={{ scaleX: 0 }}
      animate={{
        scaleX: 1,
        rotateX: 15,
      }}
      transition={{
        duration: 2,
        ease: "easeInOut",
      }}
    />
  );
};

const PopularLinksContainer = styled(Box)(({ theme }) => ({
  background: 'linear-gradient(135% 2947B3 0%, #1e3178 100%)',
  padding: '20px',
  borderRadius: '10px',
  width: '45%',
  boxShadow: '0 8px 32px 0 rgba(25, 44, 130, 0.37)',
  backdropFilter: 'blur(4px)',
  margin: '0 auto',
  marginTop: '20px',
  marginLeft: 'auto',
  marginRight: 'auto',
  textAlign: 'left',
  // Two cards side by side on desktop (like News & Blogs); one per row on
  // smaller screens.
  display: 'grid',
  gridTemplateColumns: '1fr',
  gap: '15px',
  alignItems: 'stretch',
  [theme.breakpoints.up('md')]: {
    gridTemplateColumns: 'repeat(2, 1fr)',
    width: '70%',
  },
  [theme.breakpoints.down('lg')]: {
    width: '70%',
  },
  [theme.breakpoints.down('sm')]: {
    width: '94%',
    margin: '10px auto',
    padding: '8px',
  }
}));

const LinkCard = styled(motion.div)(({ theme }) => ({
  background: 'linear-gradient(135deg, rgba(41, 71, 179, 0.3) 0%, rgba(30, 49, 120, 0.2) 100%)',
  border: '2px solid rgba(255, 255, 255, 0.4)',
  borderRadius: '8px',
  padding: '20px',
  marginBottom: 0, // grid gap provides the spacing
  height: '100%',
  boxSizing: 'border-box',
  position: 'relative',
  overflow: 'hidden',
  transition: 'all 0.3s ease',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  gap: '8px',
  boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
  '&:hover': {
    borderColor: 'rgba(255, 255, 255, 0.8)',
    backgroundColor: 'rgba(41, 71, 179, 0.4)',
    boxShadow: '0 8px 16px rgba(0, 0, 0, 0.2)',
  }
}));

// Helper function to extract clean name from URL
const extractLinkName = (url) => {
  try {
    const domain = new URL(url).hostname.replace('www.', '');
    return domain.split('.')[0].charAt(0).toUpperCase() + domain.split('.')[0].slice(1);
  } catch (error) {
    return url.split('/')[2] || url;
  }
};

function PopularLinks() {
  const [links, setLinks] = useState([]);
  const [hoveredIndex, setHoveredIndex] = useState(null);
  // Pointer is somewhere in the section (title, bar, or cards): the green
  // glow bar brightens while browsing here and dims on leave.
  const [sectionHovered, setSectionHovered] = useState(false);

  const navigate = useNavigate();
  const handleCardClick = () => {
    navigate('/info-center', { state: { selectTab: 'Popular Links' } });
  };

  useEffect(() => {
    const fetchLinks = async () => {
      try {
        const response = await axios.get('/api/popular-links');
        console.log('API Response:', response.data);
        setLinks(response.data);
      } catch (error) {
        console.error('Error fetching popular links:', error);
      }
    };
    fetchLinks();
  }, []);

  const cardVariants = {
    initial: {
      opacity: 0,
      y: 20,
      scale: 0.95
    },
    animate: {
      opacity: 1,
      y: 0,
      scale: 1,
      transition: {
        type: "spring",
        stiffness: 100,
        damping: 15
      }
    },
    hover: {
      scale: 1.02,
      transition: {
        duration: 0.2
      }
    }
  };

  const iconVariants = {
    initial: {
      rotate: 0
    },
    hover: {
      rotate: 360,
      transition: {
        duration: 2,
        ease: "linear",
      }
    }
  };

  if (links.length === 0) {
    return (
      <Typography sx={{ color: '#FFFFFF', textAlign: 'center', marginTop: '20px' }}>
        Loading...
      </Typography>
    );
  }

  return (
    <div
      onMouseEnter={() => setSectionHovered(true)}
      onMouseLeave={() => setSectionHovered(false)}
    >
      {/* Title and divider */}
      <Box sx={{ textAlign: 'center', marginTop: '20px' }}>
        <RetroText>Popular Links</RetroText>
        <GreenMotionDivider bright={sectionHovered} />
      </Box>

      <PopularLinksContainer>
        {links.slice(0, 10).map((link, index) => {
          const IconComponent = categoryIcons[link.category] || categoryIcons['Other'];
          const linkName = extractLinkName(link.URL);
          
          return (
            <div key={link._id} style={{ height: '100%' }}>
              <LinkCard
                variants={cardVariants}
                initial="initial"
                animate="animate"
                whileHover="hover"
                onHoverStart={() => setHoveredIndex(index)}
                onHoverEnd={() => setHoveredIndex(null)}
                onClick={handleCardClick}
                style={{ cursor: 'pointer' }}
              >
                {/* Enhanced Title/Hook at the top */}
                <TitleHook
                  whileHover={{
                    scale: 1.05,
                    transition: { duration: 0.2 }
                  }}
                >
                  {link.title || `Discover ${linkName}`}
                </TitleHook>

                {/* Category with single icon */}
                <CategorySection>
                  <CategoryIcon
                    variants={iconVariants}
                    initial="initial"
                    whileHover="hover"
                  >
                    <IconComponent size={24} color="rgba(255, 255, 255, 0.9)" />
                  </CategoryIcon>
                  <CategoryText>{link.category || 'Other'}</CategoryText>
                </CategorySection>

                {/* Clean link name */}
                <LinkName
                  onClick={(e) => {
                    e.stopPropagation();
                    window.open(link.URL, '_blank');
                  }}
                  whileHover={{
                    scale: 1.05,
                    transition: { duration: 0.2 }
                  }}
                  whileTap={{ scale: 0.95 }}
                >
                  {linkName}
                </LinkName>

                {/* Full URL link */}
                <FullUrlLink
                  href={link.URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={(e) => e.stopPropagation()}
                  whileHover={{
                    scale: 1.02,
                    transition: { duration: 0.2 }
                  }}
                >
                  {link.URL}
                </FullUrlLink>

                {/* Description */}
                {link.description && (
                  <Description>{link.description}</Description>
                )}
              </LinkCard>
            </div>
          );
        })}
      </PopularLinksContainer>
    </div>
  );
}

export default PopularLinks;

